import React, { ReactNode, useMemo } from 'react';
import { SelectField } from '@ovotech/element-v4';
import { AddressLookup } from '@/src/types/Address';
import { deterministicStringify } from '@/src/utils/deterministicStringify';

type AddressFieldProps = {
  id: string;
  value: AddressLookup | undefined;
  error: ReactNode;
  addresses?: AddressLookup[];
  onValueChange: (address: AddressLookup | undefined) => void;
};

export const AddressField = ({
  id,
  error,
  value,
  addresses,
  onValueChange,
}: AddressFieldProps) => {
  const createLabelFromAddressCount = (count: number): string => {
    if (count === 0) {
      return 'No address found';
    }
    if (count === 1) {
      return '1 Address found';
    }
    return `${count} Addresses found`;
  };

  const options = useMemo(() => {
    const addressOptions = (addresses || []).map(address => ({
      label: address.fullAddress || '',
      value: deterministicStringify(address),
    }));
    const firstOption = {
      label: createLabelFromAddressCount(addressOptions.length),
      value: '',
    };
    return [firstOption, ...addressOptions];
  }, [addresses]);

  const handleValueChange = (value?: string) => {
    try {
      const address: AddressLookup = JSON.parse(value || '');
      onValueChange(address);
    } catch {
      onValueChange(undefined);
    }
  };

  return (
    <SelectField
      id={id}
      label="Address"
      fullWidth={true}
      value={deterministicStringify(value || {})}
      error={error}
      options={options}
      onValueChange={handleValueChange}
    />
  );
};
